import React, { useEffect, useState } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import {
  IonPage,
  IonContent,
  IonFabButton,
  IonIcon,
  IonToast,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButton,
  IonFooter,
  IonFab,
} from "@ionic/react";
import { createOutline, settingsOutline } from "ionicons/icons";

import api, { getAuthToken } from "../services/api";
import CardPreview from "../components/CardPreview";
import PageBackground from "../components/PageBackground";
import { BusinessCardPreview } from "../components/cards/business-card-preview";
import EcardCtaButton from "../components/ui/EcardCtaButton";
import DownloadModal from "../components/DownloadModal";

const CardView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [card, setCard] = useState<any>(null);
  const [isOwner, setIsOwner] = useState(false);
  const [toast, setToast] = useState<
    { show: boolean; message: string } | undefined
  >();
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const history = useHistory();
  const location = useLocation();

  // Check if we're in browser mode (accessed via QR code or "Apri in New Tab")
  const searchParams = new URLSearchParams(location.search);
  const isBrowserMode = searchParams.get("mode") === "browser";

  useEffect(() => {
    const fetchCard = async () => {
      try {
        const res = await api.get(`/cards/${id}`, {
          public: true,
          skipAuthRedirect: true,
        } as any);
        setCard(res.data);
      } catch (error) {
        console.error("Error fetching card:", error);
      }
    };

    const checkOwnership = async () => {
      if (!getAuthToken()) {
        setIsOwner(false);
        return;
      }

      try {
        const res = await api.get("/cards/me", { skipAuthRedirect: true } as any);
        setIsOwner(res.data?.id === id);
      } catch (error) {
        console.log("Not authenticated or no card found");
      }
    };

    if (id) {
      fetchCard();
      checkOwnership();
    }
  }, [id]);

  if (!card) {
    return (
      <IonPage>
        <IonContent fullscreen style={{ "--background": "transparent" }}>
          <div
            style={{
              height: "100%",
              minWidth: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.2rem",
            }}
          >
            Loading...
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonContent
        style={{
          paddingTop: "var(--ion-safe-area-top)",
        }}
      >
        <BusinessCardPreview
          card={card}
          showActions={true}
          hasSafeArea={true}
        ></BusinessCardPreview>

        {/* Sticky Edit FAB for owners - only in app mode - positioned outside content */}
        {!isBrowserMode && isOwner && (
          <IonFab vertical="bottom" horizontal="end" slot="fixed">
            <IonFabButton
              onClick={() => history.push("/create")}
              className="lg:me-4"
              style={{
                "--background": "#212735",
                "--color": "white",
                "--box-shadow": "0 4px 20px rgba(0,0,0,0.3)",
                width: "56px",
                height: "56px",
                opacity: 0.6,
              }}
            >
              <IonIcon icon={createOutline} />
            </IonFabButton>
          </IonFab>
        )}

        {!isOwner && !getAuthToken() && (
          <div className="fixed bottom-[4.5rem] left-0 right-0 flex justify-center px-4 z-[5000] pointer-events-none">
            <EcardCtaButton
              className="pointer-events-auto"
              onClick={() => setDownloadModalOpen(true)}
            >
              Crea la tua Ecard gratis!
            </EcardCtaButton>
          </div>
        )}

        <DownloadModal
          isOpen={downloadModalOpen}
          onClose={() => setDownloadModalOpen(false)}
        />
      </IonContent>

      <IonFooter
        style={{
          "--background": "transparent",
          borderTop: "1px solid rgba(255,255,255,0.15)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            padding: "10px 0",
          }}
        >
          <a
            href="https://www.effai.it"
            target="_blank"
            rel="noopener noreferrer"
            className="gradient-text"
          >
            <img
              className="h-4 xs:h-6 sm:h-8"
              src="/assets/long_light.png"
              alt="logo"
              height={24}
              style={{ borderRadius: 4 }}
            />
          </a>

          <span
            style={{
              color: "rgba(255,255,255,0.8)",
            }}
            className="text-[0.6rem] xs:text-xs md:text-base"
          >
            Intelligenza Artificiale su misura per te!{" "}
            <a
              href="https://www.effai.it"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#00dbd4",
                textDecoration: "underline",
              }}
            >
              Scopri come
            </a>
          </span>
        </div>
      </IonFooter>
    </IonPage>
  );
};

export default CardView;
